import {createContext, useContext, useMemo, useState} from 'react'

import dynamic from 'next/dynamic'
import singletonRouter from 'next/router'
import {useLocalStorage, useMedia, useUpdateEffect} from 'react-use'
import {ParsedUrlQuery} from 'querystring'
import {isArray, mapValues} from 'lodash'
import {
  Configure,
  ConfigureProps,
  InstantSearch,
  InstantSearchServerState,
  InstantSearchSSRProvider,
} from 'react-instantsearch'
import {createInstantSearchRouterNext} from 'react-instantsearch-router-nextjs'

import {useEvents} from '@festi/common/contexts'
import {gridTheme} from '@festi/common/themes'
import settings from '@festi/common/constants/settings'
import {searchClient, searchIndexes} from '@festi/common/api/algolia'

import {priceRangeAttribute, ruleContexts} from '../utilites/refinement'
import {GridType} from 'src/components/cards/redesign/NewProductCard'
import {resultsOptions} from '../components/SelectSize'

const baseIndex = `${searchIndexes.productVariants}_popular`

const VirtualSearchBox = dynamic(
  () => import('../components/VirtualSearchBox'),
  {
    ssr: false,
  },
)

const RefinementDrawer = dynamic(
  () => import('../components/RefinementDrawer'),
  {
    ssr: false,
  },
)

interface CustomUiState {
  q?: string
  sort?: string
  [list: string]: string
}

export const AlgoliaContext = createContext<Partial<AlgoliaContextProps>>({})

interface AlgoliaProviderProps {
  id?: number
  children: React.ReactNode
  attrNameMap?: Record<string, string>
  initialQuery: ParsedUrlQuery
  serverState?: InstantSearchServerState | object
}

export function AlgoliaProvider({
  id,
  children,
  attrNameMap,
  serverState,
  initialQuery,
}: AlgoliaProviderProps) {
  const {userToken} = useEvents()

  const isMobile = useMedia(`(max-width: ${gridTheme.breakpoints.md}px)`, false)
  const [openRefine, setOpenRefine] = useState(false)

  const [mapQuery, setMapQuery] = useState(
    mapValues(initialQuery, (v) => (isArray(v) ? v[0] : v)),
  )

  const [indexName, setIndexName] = useState<string>(
    `${searchIndexes.productVariants}_${mapQuery?.sort || 'popular'}`,
  )

  const [hitsPerPage, setHitsPerPage] = useLocalStorage<number>(
    'hitsPerPage',
    resultsOptions[0].value,
  )
  const [gridType, setGridType] = useLocalStorage<GridType>(
    'gridType',
    'normal',
  )
  const [numberOfHits, setNumberOfHits] = useState<number>(0)

  const categoriesId = id ? `categories:${id}` : ''
  const channelListPath = `${settings.channelListingPath}: true`
  const filters = [categoriesId, channelListPath].filter(Boolean).join(' AND ')
  const facetFilters = [`${settings.channelListingPath}:true`]

  const page = useMemo(() => {
    const parsedPage = parseInt(mapQuery?.page as string)

    if (isNaN(parsedPage)) {
      return 0
    }

    return Math.max(0, parsedPage - 1)
  }, [mapQuery?.page])

  const configure: ConfigureProps = {
    page,
    filters,
    facetFilters,
    hitsPerPage,
    userToken,
    ruleContexts,
  }

  const routing =
    typeof window !== 'undefined'
      ? {
          router: createInstantSearchRouterNext({
            singletonRouter,
            routerOptions: {
              cleanUrlOnDispose: false,
            },
          }),
          stateMapping: {
            stateToRoute: (uiState): any => {
              const state = uiState[indexName]

              const refinements = !!state?.refinementList
                ? Object.keys(state.refinementList).reduce((acc, key) => {
                    acc[key] = state.refinementList[key]
                      .map((value) => value.replace(',', '..'))
                      .join(',')
                    return acc
                  }, {})
                : {}

              const rangeSplit = state?.range?.[priceRangeAttribute]?.split(':')

              const priceFrom =
                !rangeSplit?.[0] || rangeSplit?.[0] === 'undefined'
                  ? undefined
                  : rangeSplit?.[0]
              const priceTo =
                !rangeSplit?.[1] || rangeSplit?.[1] === 'undefined'
                  ? undefined
                  : rangeSplit?.[1]

              const modifiedState: CustomUiState = {
                q: mapQuery.q,
                sort: mapQuery.sort,
                page: mapQuery.page,
                priceFrom,
                priceTo,
                ...refinements,
              }

              return modifiedState
            },
            routeToState: (routeState) => {
              const restoreAttributes = Object.keys(routeState).reduce(
                (acc, key) => {
                  if (
                    key.startsWith('attributes') ||
                    key.startsWith('categories')
                  ) {
                    acc[key] = routeState[key]
                      .split(',')
                      .map((value) => value.replace('..', ','))
                  }
                  return acc
                },
                {},
              )

              const restoreState = {
                [indexName]: {
                  query: routeState.q,
                  sort: routeState.sort,
                  page: routeState.page ? parseInt(routeState.page) : null,
                  configure,
                  range: {
                    [priceRangeAttribute]: `${routeState.priceFrom || ''}:${
                      routeState.priceTo || ''
                    }`,
                  },
                  refinementList: {
                    ...restoreAttributes,
                  },
                },
              }

              return restoreState
            },
          },
        }
      : undefined

  useUpdateEffect(() => {
    if (isMobile) {
      setGridType('normal')
    }
  }, [isMobile])

  useUpdateEffect(() => {
    setIndexName(
      `${searchIndexes.productVariants}_${mapQuery?.sort || 'popular'}`,
    )
  }, [mapQuery?.sort])

  return (
    <AlgoliaContext.Provider
      value={{
        initialQuery: mapQuery.q as string,
        openRefine,
        page,
        gridType,
        hitsPerPage,
        numberOfHits,
        setGridType,
        setHitsPerPage,
        setNumberOfHits,
        setOpenRefine,
      }}
    >
      <InstantSearchSSRProvider
        {...(baseIndex === indexName ? serverState : undefined)}
      >
        <InstantSearch
          key={[mapQuery.q, indexName].join('-')}
          indexName={indexName}
          searchClient={searchClient}
          insights
          future={{
            preserveSharedStateOnUnmount: true,
            persistHierarchicalRootCount: true,
          }}
          routing={routing}
        >
          <Configure {...configure} />

          {children}

          <RefinementDrawer
            isOpen={openRefine}
            attrNameMap={attrNameMap}
            onRequestClose={() => setOpenRefine(false)}
          />

          <VirtualSearchBox mapQuery={mapQuery} setMapQuery={setMapQuery} />
        </InstantSearch>
      </InstantSearchSSRProvider>
    </AlgoliaContext.Provider>
  )
}

export interface AlgoliaContextProps {
  initialQuery?: string
  openRefine: boolean
  page: number
  gridType: GridType
  hitsPerPage: number
  numberOfHits: number
  setGridType: (gridType: GridType) => void
  setHitsPerPage: (hitsPerPage: number) => void
  setNumberOfHits: (numberOfHits: number) => void
  setOpenRefine: (open: boolean) => void
}

export function useAlgoliaContext() {
  return useContext(AlgoliaContext) as AlgoliaContextProps
}
